import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../../setup/redux/hooks";
import type { RootState } from "../../../../setup/redux/store";
import { useTheme } from "src/context/ThemeContext";
import { StatusTabs } from "../../product/components/listing/shared/StatusTabs";
import { MerchantProductTable } from "../components/listing/desktop/MerchantProductTable";
import { FilterPanel } from "../components/listing/shared/FilterPanel";
import { ActionButtons } from "../components/listing/shared/ActionButtons";
import { MerchantProductCard } from "../components/listing/mobile/MerchantProductCard";
import { MerchantProductLoadingSkeleton } from "../components/listing/mobile/MerchantProductLoadingSkeleton";
import type {
  StockUpdateRequest,
  PriceUpdateRequest,
} from "../types";
import {
  fetchMerchantProducts,
  updateProductStock,
  updateProductPrice,
  setFilters,
  setSearchTerm,
  setProductTypeFilter,
  setPriceRange,
  setSalesRange,
  setPage,
  setPageSize,
  clearError,
} from "../redux/merchantProductSlice";
import {
  transformApiToUIProduct,
  UIMerchantProduct,
} from "../utils/dataTransformers";
import { transformMerchantProductToCard } from "../utils/merchantProductCardTransformer";
import ContentHeader from "../../../layouts/content/ContentHeader";

const MerchantProductPage: React.FC = () => {
  const { theme } = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Get state from Redux store
  const {
    products: reduxProducts,
    loading,
    error,
    filters: reduxFilters,
    pagination: reduxPagination,
  } = useSelector((state: RootState) => state.MerchantProduct);

  const [searchTerm, setSearchTermLocal] = useState<string>("");
  
  // Mobile pagination state
  const [mobileItemsToShow, setMobileItemsToShow] = useState(20);

  // Transform API products to UI format with error handling
  const products: UIMerchantProduct[] = reduxProducts
    .map((product) => {
      try {
        return transformApiToUIProduct(product);
      } catch (error) {
        console.error("❌ Failed to transform product:", product?.id, error);
        return null;
      }
    })
    .filter((product) => product !== null) as UIMerchantProduct[];

  console.log("📊 Redux Products:", reduxProducts.length, reduxProducts);
  console.log("🎨 Transformed Products:", products.length, products);

  // Initialize data on component mount
  useEffect(() => {
    console.log("🌐 Attempting to fetch products from API...");
    dispatch(
      fetchMerchantProducts({
        filters: reduxFilters,
        page: reduxPagination.currentPage,
        pageSize: reduxPagination.pageSize,
      }) as any
    );
  }, [dispatch]);

  // Sync local search term with Redux
  useEffect(() => {
    setSearchTermLocal(reduxFilters.search);
  }, [reduxFilters.search]);

  // Calculate mobile visible products and load more
  const mobileVisibleProducts = products.slice(0, mobileItemsToShow);
  const hasMoreItems = mobileItemsToShow < reduxPagination.totalItems;

  const handleLoadMore = () => {
    setMobileItemsToShow(prev => Math.min(prev + 20, reduxPagination.totalItems));
  };

  // Mobile action handler
  const handleMobileAction = (action: string, productId: string) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    switch (action) {
      case "update-stock":
        handleStockUpdate({ productId, newQuantity: product.stock.quantity });
        break;
      case "update-price": 
        handlePriceUpdate({ productId, newPrice: product.price.current });
        break;
      case "view-details":
        handleRowClick(product);
        break;
      default:
        console.log(`Unknown mobile action: ${action} for product ${productId}`);
    }
  };


  const handleFiltersChange = (
    newFilters: Partial<{
      priceRange: { min: number | null; max: number | null };
      salesRange: { min: number | null; max: number | null };
    }>
  ) => {
    // Update Redux filters
    if (newFilters.priceRange) {
      dispatch(setPriceRange(newFilters.priceRange));
    }
    if (newFilters.salesRange) {
      dispatch(setSalesRange(newFilters.salesRange));
    }

    // Fetch new data with updated filters
    dispatch(
      fetchMerchantProducts({
        filters: { ...reduxFilters, ...newFilters },
        page: 1,
        pageSize: reduxPagination.pageSize,
      }) as any
    );

    // Note: Applied filters will be automatically updated by useEffect watching Redux state
  };


  const handleStockUpdate = (request: StockUpdateRequest) => {
    dispatch(
      updateProductStock({
        productId: request.productId,
        quantity: request.newQuantity,
      }) as any
    );
  };

  const handlePriceUpdate = (request: PriceUpdateRequest) => {
    dispatch(
      updateProductPrice({
        productId: request.productId,
        price: request.newPrice,
      }) as any
    );
  };

  const handlePageChange = (page: number) => {
    dispatch(setPage(page));
    dispatch(
      fetchMerchantProducts({
        filters: reduxFilters,
        page,
        pageSize: reduxPagination.pageSize,
      }) as any
    );
  };

  const handlePageSizeChange = (pageSize: number) => {
    dispatch(setPageSize(pageSize));
    dispatch(
      fetchMerchantProducts({
        filters: reduxFilters,
        page: 1,
        pageSize,
      }) as any
    );
  };

  const handleSearchChange = (searchTerm: string) => {
    setSearchTermLocal(searchTerm);
    dispatch(setSearchTerm(searchTerm));
    dispatch(
      fetchMerchantProducts({
        filters: { ...reduxFilters, search: searchTerm },
        page: 1,
        pageSize: reduxPagination.pageSize,
      }) as any
    );
  };

  const handleSearch = (searchTerm: string) => {
    handleSearchChange(searchTerm);
  };

  const handleRefresh = () => {
    dispatch(fetchMerchantProducts({
      filters: reduxFilters,
      page: reduxPagination.currentPage,
      pageSize: reduxPagination.pageSize,
      force: true,
    }) as any);
  };

  const handleProductTypeChange = (
    productType: "all" | "variant" | "single" | "bundle"
  ) => {
    dispatch(setProductTypeFilter(productType));
    dispatch(
      fetchMerchantProducts({
        filters: { ...reduxFilters, productType },
        page: 1,
        pageSize: reduxPagination.pageSize,
      }) as any
    );
  };

  const handleImport = () => {
    console.log("Import products");
    // Open import dialog or file picker
  };

  const handleExport = () => {
    console.log("Export products");
    // Export current products to CSV/Excel
  };

  const handleRowClick = (product: UIMerchantProduct) => {
    console.log("Product clicked:", product.name);
    // Navigate to edit product page
    navigate(`/product/merchant/edit/${product.id}`);
  };

  const handleEditProduct = (product: UIMerchantProduct) => {
    console.log("Edit product:", product.name);
    // Navigate to edit product page
    navigate(`/product/merchant/edit/${product.id}`);
  };

  const handleAddProduct = () => {
    // Navigate to add product page
    navigate('/product/merchant/create');
  };

  // The selected tab's total is already returned by the list request.
  const productTypeTabs = [
    { key: "all", label: "All" },
    { key: "variant", label: "Variant" },
    { key: "single", label: "Single" },
    { key: "bundle", label: "Bundle" },
  ] as const;

  // Improved fallback logic - detect malformed API data
  const hasValidApiData =
    products.length > 0 && products.some((p) => p.name && p.id);
  const displayProducts = products ?? [];

  if (!hasValidApiData && products.length > 0) {
    console.warn("⚠️ API returned malformed data, falling back to mock data");
  }

  return (
    <div data-testid="page-container" className="tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
      {/* Page Header */}
      <ContentHeader
        title="Merchant Products"
        searchValue={searchTerm}
        onSearchChange={handleSearchChange}
        onSearchSubmit={handleSearch}
        buttons={[
          {
            text: "Refresh",
            onClick: handleRefresh,
            variant: "secondary",
            icon: "refresh",
          },
          {
            text: "Import Products",
            onClick: handleImport,
            variant: "secondary",
            icon: "upload",
          },
          {
            text: "Export Products",
            onClick: handleExport,
            variant: "secondary",
            icon: "download",
          },
          {
            text: "Add Product Merchant",
            onClick: handleAddProduct,
            variant: "primary",
            icon: "add",
          },
        ]}
      />

      <div
        data-testid="main-content"
        className={`tw-flex-1 tw-overflow-y-auto tw-p-4 tw-pb-20 md:tw-p-6 ${
          theme === "light" ? "tw-bg-gray-50" : "tw-bg-slate-900"
        }`}
      >
        <div className="tw-flex tw-flex-col tw-gap-4" data-testid="content-wrapper">
          {/* Error Display */}
          {error && (
            <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-md tw-p-4">
              <div className="tw-flex">
                <div className="tw-text-sm tw-text-red-800">{error}</div>
                <button
                  onClick={() => dispatch(clearError())}
                  className="tw-ml-auto tw-text-red-600 hover:tw-text-red-800"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {/* Filter and Action Section */}
          <div className="tw-flex tw-gap-3 tw-overflow-x-visible">
            <FilterPanel
              filters={{
                search: reduxFilters.search,
                priceRange: reduxFilters.priceRange,
                salesRange: reduxFilters.salesRange,
              }}
              onFiltersChange={handleFiltersChange}
              data-testid="filter-panel"
            />
          </div>


          {/* Product Type Tabs Section */}
          <div className="tw-overflow-x-auto tw-scrollbar-hide">
            <div
              className={`tw-flex tw-border-b ${
                theme === "light"
                  ? "tw-border-slate-200"
                  : "tw-border-slate-700"
              }`}
            >
              <StatusTabs
                activeStatus={(reduxFilters.productType as any) || "all"}
                onStatusChange={handleProductTypeChange as any}
                tabs={productTypeTabs as any}
                showCounts={false}
                disabled={loading}
                data-testid="status-tabs"
              />
            </div>
          </div>

          {/* Product Table - Desktop only */}
          <div className="tw-hidden md:tw-block">
            <MerchantProductTable
              products={displayProducts}
              pagination={reduxPagination}
              loading={loading}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onStockUpdate={handleStockUpdate}
              onPriceUpdate={handlePriceUpdate}
              onRowClick={handleRowClick}
              onEditProduct={handleEditProduct}
              showVariants={true}
              data-testid="merchant-product-table"
            />
          </div>

          {/* Mobile View - Enhanced Cards */}
          <div className="tw-block md:tw-hidden tw-flex-1 tw-overflow-y-auto">
            {loading ? (
              // Enhanced Mobile Loading State
              <MerchantProductLoadingSkeleton count={3} />
            ) : displayProducts.length === 0 ? (
              // Mobile Empty State
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-6 tw-py-16 tw-text-center tw-min-h-[50vh]">
                <div
                  className={`tw-mb-6 tw-flex tw-h-20 tw-w-20 tw-items-center tw-justify-center tw-rounded-full ${
                    theme === "light" ? "tw-bg-gray-50" : "tw-bg-slate-700"
                  }`}
                >
                  <span className="material-symbols-outlined tw-text-5xl tw-text-gray-400">
                    search_off
                  </span>
                </div>
                <h3
                  className={`tw-text-xl tw-font-semibold tw-mb-2 ${
                    theme === "light" ? "tw-text-gray-900" : "tw-text-slate-100"
                  }`}
                >
                  No Products Found
                </h3>
                <p
                  className={`tw-text-sm tw-mb-6 tw-max-w-sm ${
                    theme === "light" ? "tw-text-gray-500" : "tw-text-slate-400"
                  }`}
                >
                  Your search criteria didn't match any products. Try clearing
                  filters or adding a new product.
                </p>
                <button
                  onClick={handleAddProduct}
                  className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-primary tw-px-6 tw-py-3 tw-text-sm tw-font-medium tw-text-white tw-shadow-sm hover:tw-bg-primary/90 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-ring-offset-2"
                >
                  <span className="material-symbols-outlined tw-mr-2 tw-text-xl">
                    add_circle
                  </span>
                  Add Product
                </button>
              </div>
            ) : (
              <div className="tw-flex tw-flex-col tw-gap-4">
                {/* Enhanced Product Cards */}
                {mobileVisibleProducts.map((product) => (
                  <MerchantProductCard
                    key={product.id}
                    {...transformMerchantProductToCard(product, handleMobileAction)}
                  />
                ))}

                {/* Load More Button */}
                {hasMoreItems && (
                  <div className="tw-flex tw-justify-center tw-pt-4">
                    <button
                      onClick={handleLoadMore}
                      className="tw-flex tw-items-center tw-gap-2 tw-px-6 tw-py-3 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-shadow-sm dark:tw-bg-slate-800 dark:tw-border-slate-600 dark:tw-text-slate-200 dark:hover:tw-bg-slate-700"
                    >
                      <span className="material-symbols-outlined tw-text-base">
                        expand_more
                      </span>
                      Load More ({reduxPagination.totalItems - mobileItemsToShow} remaining)
                    </button>
                  </div>
                )}

                {/* Mobile Items Count */}
                <div className={`tw-text-center tw-text-sm tw-pt-2 ${
                  theme === "light" ? "tw-text-gray-500" : "tw-text-slate-400"
                }`}>
                  Showing {mobileVisibleProducts.length} of {reduxPagination.totalItems} products
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MerchantProductPage;
